import { IGlobalOrganizationResponse } from '@sparelabs/api-client'
import { WebColors } from '@sparelabs/colors'
import { CaseStatusStage, ICaseResponse, ICaseStatusResponse, ICaseTypeResponse } from '@sparelabs/engage-client'
import {
  Badge,
  blueBadgeTheme,
  boxShadow,
  boxShadowStrong,
  grayBadgeTheme,
  greenBadgeTheme,
  HintContainer,
} from '@sparelabs/nutshell-legacy'
import React from 'react'
import { useAsync } from 'react-use'
import { EngageClientBuilder } from 'src/api/ApiClientBuilders'
import { CaseLoadingView } from 'src/components/LoadingView'
import { DateTimeHelper } from 'src/helpers/DateTimeHelper'
import { st } from 'src/locales/TranslationHelper'
import { useFormContext } from 'src/pages/Form/FormContext'
import { FormSubmitted } from 'src/pages/Form/FormStepTypes'
import styled from 'styled-components'

const ResourceCell = styled.div`
  height: 80px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 16px;
  background-color: ${WebColors.backgroundPrimary};
  border: 1px solid ${WebColors.borderPrimary};
  border-radius: 8px;
  box-shadow: ${boxShadow};
  cursor: pointer;
  &:hover {
    box-shadow: ${boxShadowStrong};
  }
`

const ResourceContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const BadgeContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const CaseNameContainer = styled.div`
  white-space: pre-line;
  line-height: 20px;
  font-size: 15px;
`

const NameDateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

interface IProps {
  caseData: ICaseResponse
  caseStatuses: Map<string, ICaseStatusResponse[]>
  caseTypes: ICaseTypeResponse[]
}

const caseStatusColorMap = {
  [CaseStatusStage.Todo]: grayBadgeTheme,
  [CaseStatusStage.InProgress]: blueBadgeTheme,
  [CaseStatusStage.Done]: greenBadgeTheme,
}

export const CaseCard = ({ caseData, caseStatuses, caseTypes }: IProps): JSX.Element | null => {
  const { onNextStep, setCaseId, setForm, organization, setCaseForm } = useFormContext()
  const caseType = caseTypes.find((caseType) => caseType.id === caseData.caseTypeId)
  const status = caseStatuses.get(caseData.caseTypeId)?.find((status) => status.id === caseData.statusId)
  const statusName = status?.riderInterfaceLabel ?? status?.label

  const { value, loading } = useAsync(
    async () => findCaseFormIfExists(caseData, caseType, organization),
    [caseData, caseType, organization],
  )

  if (loading) {
    return (
      <ResourceCell>
        <CaseLoadingView />
      </ResourceCell>
    )
  }

  const handleClick = () => {
    if (!caseType || !caseType?.defaultFormKey) {
      throw new Error()
    }

    setCaseForm(value?.caseForm, FormSubmitted.ThisSession)
    setForm(value?.form)
    setCaseId(caseData.id)
    onNextStep()
  }

  if (!status) {
    throw new Error()
  }

  return (
    <ResourceCell onClick={handleClick}>
      <ResourceContainer>
        <NameDateContainer>
          <CaseNameContainer>{caseType?.name}</CaseNameContainer>
          <HintContainer style={{ paddingBottom: '0px', marginTop: '0px', color: `${WebColors.contentSecondary}` }}>
            {st.forms.caseSelect.createdAt({
              dateString: DateTimeHelper.formatDate(caseData.createdAt * 1000, DateTimeHelper.timezone),
            })}
          </HintContainer>
        </NameDateContainer>
      </ResourceContainer>
      <BadgeContainer>
        <Badge badgeTheme={caseStatusColorMap[status.stage]}>{statusName}</Badge>
      </BadgeContainer>
    </ResourceCell>
  )
}

const findCaseFormIfExists = async (
  caseData: ICaseResponse,
  caseType: ICaseTypeResponse | undefined,
  organization: IGlobalOrganizationResponse,
) => {
  if (!caseType || !caseData || !organization) {
    throw new Error()
  }

  const client = EngageClientBuilder.build()

  const userCase = await client.cases.get(caseData.id)
  if (!userCase) {
    throw new Error(st.errors.noPermission())
  }

  const form = await client.public.getForm({
    formKey: caseType?.defaultFormKey ?? '',
    caseTypeKey: caseType.key,
    organizationId: organization.id,
  })

  const caseForm = (await client.caseForms.list({ caseId: caseData.id, formId: form.id })).data[0]
  return { form, caseForm: caseForm ?? undefined }
}
